import { useState, useEffect } from 'react';
import { Deal } from 'views/Deals/types';
import { geocodeAddress } from 'utils/geocoding';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { useCoordinatesCache } from './useCoordinatesCache';

type MapCoordinatesInput =
    & Pick<Deal, 'longitude' | 'latitude' | 'address' | 'city' | 'state' | 'zipCode'>
    & { useCoordinates?: boolean };

export const useMapCoordinates = (deal: MapCoordinatesInput | null) => {
    const [coordinates, setCoordinates] = useState<{
        lat: number;
        lng: number
    } | null>(null);
    const { getCachedCoordinates, setCachedCoordinates } = useCoordinatesCache();

    useEffect(() => {
        const getCoordinates = async () => {
            if (!deal) {
                setCoordinates(null);
                return;
            }

            // Prefer typed lat/lng only when the caller hasn't explicitly opted into the
            // address path. Callers that pass `useCoordinates: false` (e.g. pro forma with
            // the toggle off) want the address geocoded even if stale lat/lng linger in
            // the DB after a toggle flip.
            const shouldUseCoordinates = deal.useCoordinates !== false;
            if (
                shouldUseCoordinates
                && deal.latitude != null
                && deal.longitude != null
                && Number.isFinite(deal.latitude)
                && Number.isFinite(deal.longitude)
            ) {
                setCoordinates({ lat: deal.latitude, lng: deal.longitude });
                return;
            }

            if (deal.address || deal.city || deal.state || deal.zipCode) {
                const searchQuery = `${deal.address}, ${deal.city}, ${deal.state} ${deal.zipCode || ''}`.trim();
                const cachedCoordinates = getCachedCoordinates(searchQuery);
                if (cachedCoordinates) {
                    setCoordinates(cachedCoordinates);
                    return;
                }

                const result = await geocodeAddress(searchQuery);
                if (
                    result?.lat != null
                    && result?.lng != null
                    && Number.isFinite(result.lat)
                    && Number.isFinite(result.lng)
                ) {
                    setCachedCoordinates(searchQuery, result);
                    setCoordinates({
                        lat: result.lat,
                        lng: result.lng,
                    });
                }
            } else {
                setCoordinates(null);
            }
        };

        getCoordinates();
    }, [deal, getCachedCoordinates, setCachedCoordinates]);

    const lng = coordinates?.lng ?? DEFAULT_NYC_COORDINATES.lng;
    const lat = coordinates?.lat ?? DEFAULT_NYC_COORDINATES.lat;

    return { lat, lng, coordinates };
};
